import React, { useState } from 'react';
import { Button, Stack, Paper, IconButton, Chip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useFetchAllTutorialsQuery, useDeleteTutorialMutation } from '@/api/tutorialsApi';
import PageHeader from '@/components/PageHeader';
import TutorialFormDialog from './TutorialFormDialog';
import { Tutorial, TutorialStatus } from '@/types/tutorials';
import { statusLabels } from '@/constants/tutorials';
import { Delete01Icon, Edit03Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';

const LearningCenter: React.FC = () => {
  const { data: tutorials, isFetching } = useFetchAllTutorialsQuery();
  const [deleteTutorial] = useDeleteTutorialMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState<Tutorial | undefined>(undefined);

  const handleOpenNew = () => {
    setEditingTutorial(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (tutorial: Tutorial) => {
    setEditingTutorial(tutorial);
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro? El tutorial será eliminado para siempre.')) {
      await deleteTutorial(id);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Título',
      headerAlign: "center",
      align: "center",
      flex: 1
    },
    {
      field: 'youtubeUrl',
      headerName: 'URL',
      headerAlign: "center",
      align: "center",
      flex: 1,
      sortable: false
    },
    {
      field: 'productIds',
      headerName: 'Páginas Asociadas',
      headerAlign: "center",
      align: "center",
      flex: 1.5,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} overflow="hidden">
          {params?.value?.map((id: string) => (
            <Chip size="small" label={id} key={id} />
          ))}
        </Stack>
      )
    },
    {
      field: 'isActive',
      headerName: 'Visible',
      headerAlign: "center",
      align: "center",
      width: 100,
      renderCell: (params) => (
        <Typography>{params.value ? '✅' : '❌'}</Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const statusInfo = statusLabels.find(
          (status) => status[0] === params.value
        );
        return (
          <Chip
            size="small"
            color={params.value === TutorialStatus.Published ? "success" : "error"}
            label={statusInfo?.[1]}
          />
        )
      }
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      headerAlign: "center",
      align: "center",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton onClick={() => handleEdit(params.row)} size="small" color="primary">
            <HugeiconsIcon icon={Edit03Icon} />
          </IconButton>
          <IconButton onClick={() => handleDelete(params.row.id)} size="small" color="error">
            <HugeiconsIcon icon={Delete01Icon} />
          </IconButton>
        </Stack>
      )
    }
  ];

  return (
    <Stack p={2} spacing={2} height={"100%"}>
      <PageHeader
        title="Centro de Aprendizaje"
        action={
          <Button variant="contained" color="primary" onClick={handleOpenNew}>
            Nuevo Video
          </Button>
        }
      />

      <Paper elevation={1} sx={{ padding: 2 }}>
        <DataGrid
          rows={tutorials || []}
          columns={columns}
          loading={isFetching}
          disableRowSelectionOnClick
        />
      </Paper>

      {dialogOpen && (
        <TutorialFormDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          tutorialToEdit={editingTutorial}
        />
      )}
    </Stack>
  );
};

export default LearningCenter;
